import {format, setHours, setMinutes} from 'date-fns';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {
  formatDisplayValue,
  parseDateString,
  parseTimeString,
} from '../../usecase/clickToEditDateInputUtils';
import {ClickToEditDateInputPresenter} from './Presenter/ClickToEditDateInputPresenter';

type Props = {
  value: string | undefined;
  onSave: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  withTime?: boolean;
  /** 表示モード（ボタン）に適用するクラス名。幅の指定などに使用 */
  displayClassName?: string;
  className?: string;
  error?: string;
  /** 'outlined': フォーム用（ボーダーあり）, 'inline': インライン編集用（ボーダーなし） */
  variant?: 'outlined' | 'inline';
};

export const ClickToEditDateInputContainer: React.FC<Props> = ({
  value,
  onSave,
  placeholder = '未設定',
  disabled = false,
  withTime = false,
  displayClassName = '',
  className = '',
  error,
  variant = 'outlined',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputText, setInputText] = useState(
    formatDisplayValue(value, withTime),
  );
  const [calendarOpened, setCalendarOpened] = useState(false);
  const [calendarDate, setCalendarDate] = useState<Date | null>(
    value ? new Date(value) : null,
  );
  // カレンダーに表示する月（選択日とは独立して管理）
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    value ? new Date(value) : new Date(),
  );
  const [timeValue, setTimeValue] = useState(
    value ? format(new Date(value), 'HH:mm') : '',
  );
  const previousValueRef = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const isClearingRef = useRef(false);
  const {isPC} = useMediaQuery();

  useEffect(() => {
    setInputText(formatDisplayValue(value, withTime));
    setCalendarDate(value ? new Date(value) : null);
    setCalendarMonth(value ? new Date(value) : new Date());
    setTimeValue(value ? format(new Date(value), 'HH:mm') : '');
    previousValueRef.current = value;
  }, [value, withTime]);

  useEffect(() => {
    // SP時はキーボードを開かないようにfocusをスキップ
    if (isEditing && inputRef.current && isPC) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing, isPC]);

  /**
   * 日付を更新してonSaveを呼び出す共通処理
   */
  const updateDateAndSave = useCallback(
    (newDate: Date) => {
      const newIsoValue = newDate.toISOString();
      setInputText(formatDisplayValue(newIsoValue, withTime));
      setCalendarDate(newDate);
      setTimeValue(format(newDate, 'HH:mm'));

      if (newIsoValue !== previousValueRef.current) {
        onSave(newIsoValue);
        previousValueRef.current = newIsoValue;
      }
    },
    [withTime, onSave],
  );

  const applyTextInput = useCallback(() => {
    const parsed = parseDateString(inputText, withTime);
    if (parsed) {
      const newValue = parsed.toISOString();
      if (newValue !== previousValueRef.current) {
        onSave(newValue);
        previousValueRef.current = newValue;
      }
      setInputText(formatDisplayValue(newValue, withTime));
      setCalendarDate(parsed);
      setTimeValue(format(parsed, 'HH:mm'));
    } else if (inputText === '') {
      if (previousValueRef.current !== undefined) {
        onSave(undefined);
        previousValueRef.current = undefined;
      }
      setCalendarDate(null);
      setTimeValue('');
    } else {
      // パースに失敗した場合は元の値に戻す
      setInputText(formatDisplayValue(value, withTime));
    }
  }, [inputText, withTime, onSave, value]);

  const handleDisplayClick = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
      setCalendarOpened(true);
    }
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputText(e.target.value);
    },
    [],
  );

  const handleInputBlur = useCallback(() => {
    // カレンダーが開いている場合は編集モードを維持
    // これにより、カレンダーをクリックしても入力フィールドがblurで閉じることを防ぐ
    if (calendarOpened) return;

    applyTextInput();
    setIsEditing(false);
  }, [calendarOpened, applyTextInput]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyTextInput();
        setCalendarOpened(false);
        setIsEditing(false);
      }
      if (e.key === 'Escape') {
        setInputText(formatDisplayValue(value, withTime));
        setCalendarOpened(false);
        setIsEditing(false);
      }
    },
    [applyTextInput, value, withTime],
  );

  const handleInputMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // 既に編集中の場合はカレンダーの開閉をトグル
      if (isEditing) {
        e.preventDefault();
        setCalendarOpened(!calendarOpened);
      }
    },
    [isEditing, calendarOpened],
  );

  const handleCalendarSelect = useCallback(
    (date: Date) => {
      let newDate = date;

      // 既存の時刻を維持
      if (timeValue) {
        const {hours, minutes} = parseTimeString(timeValue);
        newDate = setMinutes(setHours(date, hours), minutes);
      }

      updateDateAndSave(newDate);
    },
    [timeValue, updateDateAndSave],
  );

  const handleMonthChange = useCallback((date: Date) => {
    setCalendarMonth(date);
  }, []);

  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setTimeValue(newTime);

      if (calendarDate && newTime) {
        const {hours, minutes} = parseTimeString(newTime);
        const newDate = setMinutes(setHours(calendarDate, hours), minutes);

        updateDateAndSave(newDate);
      }
    },
    [calendarDate, updateDateAndSave],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      isClearingRef.current = true;
      onSave(undefined);
      setInputText('');
      setCalendarDate(null);
      setTimeValue('');
      previousValueRef.current = undefined;
      setCalendarOpened(false);
      setIsEditing(false);
    },
    [onSave],
  );

  const handlePopoverClose = useCallback(() => {
    // クリアボタンによる閉じ処理の場合はスキップ
    if (isClearingRef.current) {
      isClearingRef.current = false;
      return;
    }
    setCalendarOpened(false);
    applyTextInput();
    setIsEditing(false);
  }, [applyTextInput]);

  return (
    <ClickToEditDateInputPresenter
      isEditing={isEditing}
      inputText={inputText}
      calendarOpened={calendarOpened}
      calendarMonth={calendarMonth}
      timeValue={timeValue}
      value={value}
      displayValue={formatDisplayValue(value, withTime)}
      placeholder={placeholder}
      disabled={disabled}
      withTime={withTime}
      displayClassName={displayClassName}
      className={className}
      error={error}
      variant={variant}
      inputRef={inputRef}
      onDisplayClick={handleDisplayClick}
      onInputChange={handleInputChange}
      onInputBlur={handleInputBlur}
      onInputKeyDown={handleInputKeyDown}
      onInputMouseDown={handleInputMouseDown}
      onCalendarSelect={handleCalendarSelect}
      onMonthChange={handleMonthChange}
      onTimeChange={handleTimeChange}
      onClear={handleClear}
      onPopoverClose={handlePopoverClose}
    />
  );
};
